import {
  Box,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Container,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import logo from "../assets/logo/daily-tick-logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import axios from "axios";
import theme from "../theme";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import kumuduLogo from "../assets/logo/kumudu-logo-1.png";
import { getStoreHome } from "../utils/moduleNav";

const OTPVerificationPage = () => {
  const { saveLoginData } = useContext(AutoTaskAuthContext);
  const location = useLocation();
  const mobile = location.state?.mobile;
  // Store picked on the login screen — a mobile can belong to several stores
  const storeId = location.state?.store_id ?? null;
  const userId = location.state?.user_id ?? null;
  const storeName = location.state?.store_name ?? null;
  // Carried from the store picker so this screen can say where the code is
  // taking them, not just which company. Already suppressed by the picker when
  // it would only repeat the store's name.
  const branchName = location.state?.branch_name ?? null;
  const inputRef = useRef(null);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [timer, setTimer] = useState(30);
  const inputsRef = useRef([]);
  const navigate = useNavigate();

  // 👇 Auto focus first OTP box on mount
  useEffect(() => {
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, []);

  // Snackbar config
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Close Snackbar
  const handleClose = () => {
    setSnackBarConfig((prevState) => ({ ...prevState, open: false }));
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/\D/, "");
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (index < 5 && inputsRef.current[index + 1]) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
      } else if (index > 0) {
        newOtp[index - 1] = "";
        inputsRef.current[index - 1]?.focus();
      }
      setOtp(newOtp);
    }

    if (e.key === "Enter") {
      handleVerify();
    }
  };

  const handleVerify = async () => {
    setIsDisabled(true);

    if (otp.includes("")) {
      setSnackBarConfig({
        open: true,
        message: "Please enter full OTP",
        severity: "error",
      });
      setIsDisabled(false);
      return;
    }

    const payload = {
      mobile: mobile,
      otp: otp.join(""),
      // Tells the backend which store membership to log in as
      store_id: storeId,
      user_id: userId,
      // No branch_id, deliberately. A login always lands in the branch the
      // ACCOUNT belongs to — their own for a pinned role, the store's MAIN
      // branch for a Store Head — which is exactly what the picker's chip
      // promised a moment ago (login_branch_* on the store row).
      //
      // This used to send the branch the device last used in this store, and
      // that is what made the chip lie: the label is worked out on the
      // server, which cannot know a device's history, so the row said "BTM
      // Branch" and the session opened in Kamakhya. Moving between branches
      // is something you do INSIDE a session (BranchSwitcher / switchBranch)
      // and it deliberately no longer outlives one.
    };
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/auth/validateotp`;

    try {
      const res = await axios.post(url, payload);
      const { statusCode, message, data } = res?.data;

      if (statusCode === 200) {
        await saveLoginData({
          user: data?.user,
          role: data?.user?.role,
          tasks: data?.tasks,
          templates: data?.templates || [],
          template_token: data?.template_token || "",
          company_settings: data?.company_settings || null,
          fy: data?.fy,
          // The store this login is scoped to — the one picked on the login
          // screen when the mobile belongs to several. Its is_pawn / is_travel
          // flags decide where we land, so it is cached with the session rather
          // than fetched afterwards.
          store: data?.store || null,
          // Signed session token. The hiring module authenticates every
          // request with it; drop it here and those screens 401 while the
          // rest of the app keeps working, which is a confusing failure to
          // track down later.
          token: data?.token || null,
          // The branch the server resolved for this session, and whether this
          // person may move between the store's branches.
          current_branch: data?.current_branch ?? null,
          can_switch_branch: Boolean(data?.can_switch_branch),
          // …and whether they may look at every branch at once. Dropping this
          // is what made a freshly logged-in owner miss the branch report:
          // saveLoginData writes the field either way, so an absent value was
          // stored as a hard "false" and stayed false until a branch switch
          // (which does send it) rewrote it. The switch-store and switch-branch
          // paths have always sent all three — keep them in step.
          can_view_all_branches: Boolean(data?.can_view_all_branches),
        });

        setSnackBarConfig({
          open: true,
          message: data.message || "Login successful!",
          severity: "success",
        });

        // A store that runs a module of its own (pawn, travel) opens on that
        // module's dashboard, not the Daily Tick home.
        // `replace` so the OTP screen — now spent — is not a back target.
        navigate(getStoreHome(data?.store), { replace: true });
      } else if (statusCode === 401) {
        setSnackBarConfig({
          open: true,
          message: message || "Invalid OTP. Try again",
          severity: "warning",
        });
        setOtp(["", "", "", "", "", ""]);
        setIsDisabled(false);
      } else {
        // Anything else (403 store access denied, 404, 500) used to leave the
        // screen frozen with the button disabled and no word of what happened.
        setSnackBarConfig({
          open: true,
          message: data?.message || message || "Login failed. Try again",
          severity: "error",
        });
        setIsDisabled(false);
      }
    } catch (error) {
      setIsDisabled(false);
      setSnackBarConfig({
        open: true,
        message:
          error?.response?.data?.message || "Oops! Something went wrong.",
        severity: "warning",
      });
    }
  };

  const handleResend = async () => {
    setOtp(["", "", "", "", "", ""]);
    setTimer(30);
    inputsRef.current[0]?.focus(); // 👈 Refocus first box after resend
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/auth/userlogin`;
    try {
      const res = await axios.post(url, { mobile });
      const { success, statusCode, statusMessage } = res.data;

      if (!success) {
        setSnackBarConfig({
          open: true,
          message: statusMessage || "No user found with this mobile number",
          severity: "warning",
        });
      }

      if (success) {
        if (statusCode === 1) {
          setSnackBarConfig({
            open: true,
            message: "OTP sent successfully",
            severity: "success",
          });
        } else {
          setSnackBarConfig({
            open: true,
            message: statusMessage || "Login failed.",
            severity: "error",
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "#fff",
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 6, sm: 8 },
            width: "100%",
          }}
        >
          {/* App Logo */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: "calc(100px + env(safe-area-inset-top, 24px))",
              position: "fixed",
              top: 0,
            }}
          >
            <Box
              sx={{
                width: 200,
                height: 60,
                backgroundImage: `url(${logo})`,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
            />
            <Typography
              sx={{ color: theme.lightTheme.placeholder, fontSize: 12, mt: 1 }}
            >
              Empowering People with smart Solutions
            </Typography>
          </Box>

          {/* Rest Sections */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            <Typography
              align="center"
              sx={{ fontSize: 24, fontWeight: 500, color: "black" }}
            >
              OTP Verification
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                color: "#a0a0a0",
                width: "100%",
                textAlign: "center",
                fontWeight: 300,
                mb: 1.5,
              }}
            >
              Enter the OTP sent to your mobile
              {storeName ? ` to log in to ${storeName}` : ""}
              {storeName && branchName ? ` · ${branchName}` : ""}
            </Typography>

            {/* OTP Inputs */}
            <Box
              ref={inputRef}
              onFocus={() => {
                setTimeout(() => {
                  inputRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }, 200);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                width: "100%",
              }}
            >
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputsRef.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "20px",
                      padding: "12px",
                    },
                  }}
                  sx={{
                    ...formStyle,
                    "& .MuiOutlinedInput-root": {
                      ...formStyle["& .MuiOutlinedInput-root"],
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: digit
                          ? "#008080"
                          : theme.lightTheme.textFieldBorder,
                        borderWidth: 2,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: digit
                          ? "#008080"
                          : theme.lightTheme.textFieldBorder,
                        borderWidth: 2,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#008080",
                        borderWidth: 2,
                      },
                    },
                  }}
                  disabled={isDisabled}
                />
              ))}
            </Box>

            <Typography
              sx={{
                textAlign: "center",
                alignSelf: "center",
                fontSize: 10,
                mt: -1.5,
              }}
            >
              OTP validity: <span style={{ color: "red" }}>{timer}</span>
            </Typography>

            <Typography sx={{ alignSelf: "flex-start", fontSize: 16 }}>
              Didn’t get the code?
              <span
                style={{
                  color: timer === 0 ? "#008080" : "gray",
                  textDecoration: timer === 0 ? "underline" : "none",
                  cursor: timer === 0 ? "pointer" : "not-allowed",
                  marginLeft: 5,
                  pointerEvents: timer === 0 ? "auto" : "none",
                }}
                onClick={timer === 0 ? handleResend : undefined}
              >
                Resend
              </span>
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                width: "100%",
                height: 54,
                background: "linear-gradient(90deg, #008080 0%, #4428A0 100%)",
                color: "white",
                boxShadow: `
                      inset -2px -2px 12px rgba(0,0,0,0.5),  
                      0px 4px 20px rgba(0,0,0,0.4)
                    `,
                borderRadius: 2.5,
                py: 1.25,
                cursor: "pointer",
              }}
              onClick={handleVerify}
            >
              <Typography sx={{ fontSize: 20, color: "white" }}>
                Verify & Proceed
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 24, color: "white" }} />
            </Box>

            <Box
              sx={{
                textAlign: "center",
                position: "fixed",
                bottom: "calc(76px + env(safe-area-inset-bottom, 44px))",
              }}
            >
              <img style={{ height: "32px" }} src={kumuduLogo}></img>
              <Typography sx={{ fontSize: 12, mt: 1 }}>
                Kumudu Software solutions LLP @ {new Date().getFullYear()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important",
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const formStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    height: 46,
    width: 46,
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.lightTheme.textFieldBorder,
      borderRadius: 2,
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.lightTheme.textFieldBorder,
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.lightTheme.textFieldBorder,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.borders.secondaryBorder,
  },
};

export default OTPVerificationPage;
